/**
 * BOGO single-item checkout confirmation + in-drawer free-pair prompt.
 * Soft confirm only — any error fails open to the original checkout path.
 *
 * Cart prompt follows the free-gift / GWP pattern:
 * Liquid always mounts the shell; JS syncs show/hide from /cart.js on load,
 * cart events, drawer open, and after section re-renders — not only on ATC.
 */
(function () {
  const STORAGE_KEY = 'bogo_confirm_dismissed';
  const SELECTOR_CHECKOUT =
    '#CartDrawer-Checkout, #checkout, button[name="checkout"][type="submit"], button[name="checkout"].cart__checkout-button';

  let open = false;
  let shownForOpen = false;
  let continuePublished = false;
  let pendingContinue = null;
  let lastTrigger = null;
  let bound = false;
  let promptSyncTimeout = null;
  let collectionProductIdsPromise = null;

  function reportError(err, context) {
    try {
      if (window.TrackJS && typeof window.TrackJS.track === 'function') {
        window.TrackJS.track(err);
      } else if (window.TrackJS && typeof window.TrackJS.console === 'object') {
        window.TrackJS.console.error(context || 'bogo-confirm', err);
      }
    } catch (_) {
      /* ignore */
    }
    try {
      console.error('[bogo-confirm]', context || '', err);
    } catch (_) {
      /* ignore */
    }
  }

  function publishEvent(name, data) {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(name, data);
      }
    } catch (_) {
      /* never block */
    }
    try {
      if (typeof publish === 'function') {
        publish(name, data);
      }
    } catch (_) {
      /* never block */
    }
  }

  function readConfig() {
    const nodes = document.querySelectorAll('[data-bogo-confirm-config]');
    if (!nodes.length) return null;
    let best = null;
    nodes.forEach((el) => {
      try {
        const parsed = JSON.parse(el.textContent || '{}');
        if (
          !best ||
          (Number(parsed.eligibleItemCount) || 0) > (Number(best.eligibleItemCount) || 0) ||
          ((Number(parsed.eligibleItemCount) || 0) === (Number(best.eligibleItemCount) || 0) &&
            parsed.modalEnabled &&
            !best.modalEnabled)
        ) {
          best = parsed;
        }
      } catch (err) {
        reportError(err, 'parse-config');
      }
    });
    if (best && best.endsAt && Date.now() / 1000 >= best.endsAt) best.active = false; // BOGO end time
    return best;
  }

  function sessionDismissed() {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === 'true';
    } catch (_) {
      return false;
    }
  }

  function setSessionDismissed() {
    try {
      sessionStorage.setItem(STORAGE_KEY, 'true');
    } catch (_) {
      /* private mode — one-shot via continuePublished only */
    }
  }

  function eligibleQtyFromDom() {
    let total = 0;
    document.querySelectorAll('[data-bogo-eligible="true"]').forEach((node) => {
      const qty = parseInt(node.getAttribute('data-bogo-qty') || node.getAttribute('data-quantity') || '0', 10);
      if (!Number.isNaN(qty) && qty > 0) total += qty;
    });
    return total;
  }

  function resolveEligibleCount(config) {
    const fromConfig = typeof config.eligibleItemCount === 'number' ? config.eligibleItemCount : 0;
    const fromDom = eligibleQtyFromDom();
    return Math.max(fromConfig, fromDom);
  }

  function shouldShow(config) {
    if (!config || !config.active || !config.modalEnabled) return false;
    if (sessionDismissed()) return false;
    const count = resolveEligibleCount(config);
    if (!count || count % 2 === 0) return false;
    // keep shown analytics accurate
    config.eligibleItemCount = count;
    return true;
  }

  function root() {
    return document.querySelector('[data-bogo-confirm]');
  }

  function dialog() {
    return document.querySelector('[data-bogo-confirm-dialog]');
  }

  function reduceMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function lockScroll(lock) {
    document.documentElement.classList.toggle('bogo-confirm-open', lock);
    document.body.classList.toggle('bogo-confirm-open', lock);
  }

  function buildContinue(trigger) {
    return function continueCheckout() {
      const btn = trigger;
      if (!btn || !btn.isConnected) {
        // Same control by id first, so a decline never resumes as the protection button.
        const fallback = (btn && btn.id && document.getElementById(btn.id)) || document.querySelector(SELECTOR_CHECKOUT);
        if (fallback && fallback.form) {
          fallback.form.requestSubmit
            ? fallback.form.requestSubmit(fallback)
            : fallback.click();
        }
        return;
      }
      const form = btn.form || btn.closest('form');
      if (form) {
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(btn);
        } else {
          const hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'checkout';
          hidden.value = '';
          form.appendChild(hidden);
          form.submit();
        }
      } else {
        btn.click();
      }
    };
  }

  function closeModal() {
    const el = root();
    if (!el) return;
    el.hidden = true;
    el.classList.remove('is-open');
    open = false;
    lockScroll(false);
    try {
      if (typeof removeTrapFocus === 'function') removeTrapFocus();
    } catch (_) {
      /* ignore */
    }
    if (lastTrigger && typeof lastTrigger.focus === 'function') {
      try {
        lastTrigger.focus();
      } catch (_) {
        /* ignore */
      }
    }
  }

  function openModal(config, trigger) {
    const el = root();
    const panel = dialog();
    if (!el || !panel) {
      throw new Error('bogo-confirm markup missing');
    }
    lastTrigger = trigger;
    pendingContinue = buildContinue(trigger);
    continuePublished = false;
    shownForOpen = false;
    el.hidden = false;
    lockScroll(true);
    requestAnimationFrame(() => {
      el.classList.add('is-open');
      if (reduceMotion()) el.classList.add('bogo-confirm--no-motion');
      else el.classList.remove('bogo-confirm--no-motion');
    });
    open = true;

    const title = panel.querySelector('#BogoConfirmTitle');
    const primary = panel.querySelector('[data-bogo-confirm-primary]');
    const secondary = panel.querySelector('[data-bogo-confirm-dismiss="secondary_cta"]');
    const qty = Number(config && config.eligibleItemCount) || 1;
    if (secondary) secondary.textContent = `Continue with ${qty} ${qty === 1 ? 'item' : 'items'}`;
    try {
      if (typeof trapFocus === 'function') {
        trapFocus(panel, primary || title || panel);
      } else if (primary) {
        primary.focus();
      } else if (title) {
        title.focus();
      }
    } catch (_) {
      if (primary) primary.focus();
    }

    if (!shownForOpen) {
      shownForOpen = true;
      publishEvent('bogo_confirm_shown', {
        cart_value: config.cartValue,
        currency: config.currency,
        eligible_item_count: config.eligibleItemCount,
      });
    }
  }

  function acknowledgeAndCheckout(method, config) {
    if (continuePublished) return;
    continuePublished = true;
    setSessionDismissed();
    publishEvent('bogo_confirm_continue_single', {
      cart_value: config ? config.cartValue : undefined,
      currency: config ? config.currency : undefined,
      dismiss_method: method,
    });
    const resume = pendingContinue;
    pendingContinue = null;
    closeModal();
    if (typeof resume === 'function') {
      try {
        resume();
      } catch (err) {
        reportError(err, 'resume-checkout');
        window.location.href = (window.routes && routes.cart_url) || '/cart';
      }
    }
  }

  function pickFreePair(config) {
    publishEvent('bogo_confirm_add_free', {
      cart_value: config.cartValue,
      currency: config.currency,
    });
    pendingContinue = null;
    closeModal();

    const url = config.collectionUrl;
    if (url) {
      window.location.href = url;
      return;
    }
    // No collection configured — fail open to checkout rather than stranding
    setSessionDismissed();
    const trigger = lastTrigger;
    const resume = buildContinue(trigger);
    try {
      resume();
    } catch (err) {
      reportError(err, 'pick-free-fallback-checkout');
    }
  }

  function onCheckoutIntent(event) {
    try {
      const trigger = event.target.closest(SELECTOR_CHECKOUT);
      if (!trigger) return;
      if (trigger.disabled) return;
      // Ignore accelerated checkout widgets
      if (trigger.closest('.additional-checkout-buttons, .dynamic-checkout__content, shopify-accelerated-checkout')) {
        return;
      }

      const config = readConfig();
      if (!shouldShow(config)) return;

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

      try {
        openModal(config, trigger);
      } catch (err) {
        reportError(err, 'open-modal');
        buildContinue(trigger)();
      }
    } catch (err) {
      reportError(err, 'checkout-intercept');
    }
  }

  function onKeydown(event) {
    if (!open) return;
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      const config = readConfig();
      acknowledgeAndCheckout('escape', config);
    }
  }

  function onClick(event) {
    if (!open) return;
    const dismiss = event.target.closest('[data-bogo-confirm-dismiss]');
    if (dismiss) {
      event.preventDefault();
      const method = dismiss.getAttribute('data-bogo-confirm-dismiss') || 'close_button';
      acknowledgeAndCheckout(method, readConfig());
      return;
    }
    if (event.target.closest('[data-bogo-confirm-overlay]')) {
      event.preventDefault();
      acknowledgeAndCheckout('overlay', readConfig());
      return;
    }
    if (event.target.closest('[data-bogo-confirm-primary]')) {
      event.preventDefault();
      pickFreePair(readConfig() || {});
    }
  }

  function isEyewearType(productType) {
    const type = String(productType || '').toLowerCase();
    if (!type || type.indexOf('jewel') !== -1) return false;
    return (
      type.indexOf('sunglass') !== -1 ||
      type.indexOf('eyeglass') !== -1 ||
      type.indexOf('optical') !== -1 ||
      type.indexOf('frame') !== -1
    );
  }

  function ensureCollectionProductIds(handle) {
    if (!handle) return Promise.resolve(new Set());
    if (collectionProductIdsPromise && collectionProductIdsPromise.__handle === handle) {
      return collectionProductIdsPromise;
    }
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    collectionProductIdsPromise = (async () => {
      const ids = new Set();
      let page = 1;
      const maxPages = 40;
      while (page <= maxPages) {
        const res = await fetch(
          `${root}collections/${encodeURIComponent(handle)}/products.json?limit=250&page=${page}`
        );
        if (!res.ok) break;
        const data = await res.json();
        if (!data.products || data.products.length === 0) break;
        data.products.forEach((p) => ids.add(Number(p.id)));
        if (data.products.length < 250) break;
        page += 1;
      }
      return ids;
    })();
    collectionProductIdsPromise.__handle = handle;
    return collectionProductIdsPromise;
  }

  function countEligibleFromCart(cart, idSet) {
    let count = 0;
    if (!cart || !cart.items) return 0;
    cart.items.forEach((item) => {
      const op = window.JamesOroProtection;
      if (op && op.isProtectionLine ? op.isProtectionLine(item) : item.vendor === 'Order Protection') return;
      if (item.properties && item.properties._auto_added) return;
      // Only products in the BOGO collection count, matching the discount's
      // buy/get scope (cases, cleaning kits and gift cards are outside it).
      // If the collection fetch failed (empty set), count the line rather than
      // hide the prompt entirely.
      if (idSet && idSet.size > 0 && !idSet.has(Number(item.product_id))) return;
      count += item.quantity || 0;
    });
    return count;
  }

  function applyPromptVisibility(shouldShow, config, eligibleCount) {
    const mount = document.querySelector('[data-bogo-cart-prompt-mount]');
    if (!mount) return;
    const prompt = mount.querySelector('[data-bogo-cart-prompt]');
    if (!prompt) return;

    mount.setAttribute('data-bogo-should-show', shouldShow ? 'true' : 'false');
    mount.setAttribute('data-bogo-eligible-qty', String(eligibleCount || 0));

    if (shouldShow) {
      prompt.hidden = false;
      prompt.removeAttribute('hidden');
      const cta = prompt.querySelector('[data-bogo-cart-prompt-cta]');
      if (cta && config && config.collectionUrl) cta.setAttribute('href', config.collectionUrl);
    } else {
      prompt.hidden = true;
      prompt.setAttribute('hidden', '');
    }
  }

  async function syncCartPromptFromCart(cart) {
    try {
      const config = readConfig();
      if (!config || !config.active || !config.modalEnabled) {
        applyPromptVisibility(false, config, 0);
        return;
      }

      const handle = config.collectionHandle || '';
      const idSet = await ensureCollectionProductIds(handle);
      const eligibleCount = countEligibleFromCart(cart, idSet);
      const shouldShow = eligibleCount > 0 && eligibleCount % 2 === 1;
      applyPromptVisibility(shouldShow, config, eligibleCount);

      // Keep checkout modal config in sync with live cart (same source as GWP).
      document.querySelectorAll('[data-bogo-confirm-config]').forEach((el) => {
        try {
          const parsed = JSON.parse(el.textContent || '{}');
          parsed.eligibleItemCount = eligibleCount;
          parsed.showCartPrompt = shouldShow;
          el.textContent = JSON.stringify(parsed);
        } catch (_) {
          /* ignore */
        }
      });
    } catch (err) {
      reportError(err, 'sync-cart-prompt');
    }
  }

  function updateBogoCartPrompt(debounced) {
    const run = () => {
      fetch('/cart.js')
        .then((res) => res.json())
        .then((cart) => syncCartPromptFromCart(cart))
        .catch((err) => reportError(err, 'fetch-cart-prompt'));
    };

    if (debounced) {
      clearTimeout(promptSyncTimeout);
      promptSyncTimeout = setTimeout(run, 300);
      return;
    }
    run();
  }

  // Expose like updateGiftProgressBar so cart-drawer renderContents/open can call it.
  window.updateBogoCartPrompt = updateBogoCartPrompt;

  function bind() {
    if (bound) return;
    bound = true;
    document.addEventListener('click', onCheckoutIntent, true);
    document.addEventListener('click', onClick, false);
    document.addEventListener('keydown', onKeydown, true);
    document.addEventListener('cart:updated', () => updateBogoCartPrompt(true));
    document.addEventListener('cart:refresh', () => updateBogoCartPrompt(true));
    updateBogoCartPrompt(false);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();
